import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import type { ErrorReporter } from './types';

interface FeatureFallbackProps {
  onReset: () => void;
}

function FeatureFallback({ onReset }: FeatureFallbackProps) {
  const { t } = useTranslation('core');
  return (
    <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-red-200 bg-red-50 p-8 text-center dark:border-red-900 dark:bg-red-950">
      <AlertTriangle className="h-8 w-8 text-red-500" />
      <div>
        <p className="text-base font-semibold">{t('errorBoundary.title')}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t('errorBoundary.description')}</p>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onReset}>
          {t('errorBoundary.retry')}
        </Button>
        <Button size="sm" onClick={() => window.location.reload()}>
          {t('errorBoundary.reload')}
        </Button>
      </div>
    </div>
  );
}

interface Props {
  children: React.ReactNode;
  reporter?: ErrorReporter;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class FeatureErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[FeatureErrorBoundary]', error, info);
    this.props.reporter?.report(error, info);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return <FeatureFallback onReset={() => this.setState({ hasError: false, error: null })} />;
    }
    return this.props.children;
  }
}
